import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import classNames from "classnames";
import { Table } from "react-bootstrap";

import { useOpenGamesContext } from "../contexts/OpenGames";
import { dateFormat } from "../lib/dateFormats";
import { SortColumns } from "../types/opengames";
import { defaultTimeFormat } from "../../../lib/utils";
import GameActionButtons from "./ActionButtons";
import { usePaginationContext } from "../../../contexts/Pagination";

const GamesTable = React.memo(() => {
    const { games, sortColumn, sortOrder, setSortColumn, setSortOrder, site } =
        useOpenGamesContext();
    const { page, itemsPerPage: gamesPerPage } = usePaginationContext();

    const paginatedGames = useMemo(() => {
        return games.slice(
            page * gamesPerPage - gamesPerPage,
            page * gamesPerPage,
        );
    }, [games, page, gamesPerPage]);

    const handleHeaderClick = (column: SortColumns) => {
        if (column !== sortColumn) {
            setSortColumn(column);
            setSortOrder("ascending");
        } else {
            setSortOrder(prev =>
                prev === "ascending" ? "descending" : "ascending",
            );
        }
    };

    return (
        <Table striped className={classNames("openGamesTable")}>
            <thead>
                <tr>
                    <th
                        onClick={() => handleHeaderClick("date")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "date" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "date" &&
                                sortOrder === "descending",
                        })}
                    >
                        Date
                    </th>
                    <th
                        onClick={() => handleHeaderClick("age")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "age" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "age" &&
                                sortOrder === "descending",
                        })}
                    >
                        Age
                    </th>
                    <th data-sortable="false">
                        {site === 1 ? "League" : "Tournament"}
                    </th>
                    <th
                        onClick={() => handleHeaderClick("location")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "location" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "location" &&
                                sortOrder === "descending",
                        })}
                    >
                        Location
                    </th>
                    {site !== 1 && <th data-sortable="false">Diamond</th>}
                    <th data-sortable="false">Action</th>
                </tr>
            </thead>
            <tbody>
                {paginatedGames.map((g, idx) => {
                    let fieldLink: string = "";
                    switch (site) {
                        case 1:
                            fieldLink = `https://www.majorybl.com/fields/${g.fieldId}`;
                            break;
                        case 2:
                            fieldLink = `https://www.wisconsinbaseballtournaments.com/fields/${g.fieldId}`;
                            break;
                        case 3:
                            fieldLink = `https://www.illinoisbaseballtournaments.com/fields/${g.fieldId}`;
                            break;
                    }
                    return (
                        <tr key={g.id || `placeholder-${idx}`}>
                            <td style={{ width: "12%" }}>
                                {dateFormat.format(new Date(g.date))}
                                <br />@{" "}
                                {defaultTimeFormat.format(new Date(g.date))}
                            </td>
                            <td style={{ width: "5%" }}>
                                {g.ageLevel.ageLevel || "TBD"}
                            </td>
                            <td style={{ width: "24%" }}>
                                <b>{g.leagueName}</b>
                                {g.site === 1 ? (
                                    <>
                                        <br />
                                        {`${g.visitor.name || "TBD"} @ ${g.home.name || "TBD"}`}
                                    </>
                                ) : (
                                    ""
                                )}
                            </td>
                            <td style={{ width: "20%" }}>
                                <Link to={fieldLink} target="_blank">
                                    {g.fieldName}
                                </Link>
                                {site === 1 && (g.diamond || g.note) && (
                                    <React.Fragment>
                                        {g.diamond && (
                                            <React.Fragment>
                                                <br />
                                                <b>Diamond:</b> {g.diamond}
                                            </React.Fragment>
                                        )}
                                        {g.note &&
                                            g.note !==
                                                g.diamond?.toString() && (
                                                <React.Fragment>
                                                    <br />
                                                    <b>Game Note:</b> {g.note}
                                                </React.Fragment>
                                            )}
                                    </React.Fragment>
                                )}
                            </td>
                            {site !== 1 && (
                                <td data-sortable="false">{g.diamond || ""}</td>
                            )}
                            <td style={{ width: "34%" }}>
                                <GameActionButtons game={g} />
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr>
                    <th>Date</th>
                    <th>Age</th>
                    <th>{site === 1 ? "League" : "Tournament"}</th>
                    <th>Location</th>
                    <th>Action</th>
                </tr>
            </tfoot>
        </Table>
    );
});

export default GamesTable;
